import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const steps = [
  {
    number: '01',
    icon: 'ri-phone-line',
    title: 'Report the Claim Immediately',
    desc: 'Contact your insurance carrier or Summit Appalachia Insurance as soon as the incident happens. Time matters — delays in reporting can complicate coverage and slow down the entire process. Don\'t wait until the next business day if it\'s urgent.',
    tip: 'Call us at 606-269-1190 — available for after-hours emergencies.',
  },
  {
    number: '02',
    icon: 'ri-file-list-3-line',
    title: 'Gather Key Information',
    desc: 'Before or while you call, start collecting the details you\'ll need. The more complete your information, the faster your claim moves forward.',
    tip: null,
    checklist: [
      'Date and time of the loss',
      'Description of what happened',
      'Photos or documentation of damage',
      'Names of any involved parties',
      'Your policy number',
      'Police report number (if applicable)',
    ],
  },
  {
    number: '03',
    icon: 'ri-send-plane-line',
    title: 'Submit the Claim',
    desc: 'We\'ll help file the claim with the carrier and make sure all details are accurate. Errors or missing information at this stage are one of the most common causes of delays — having us in your corner means it gets done right the first time.',
    tip: 'We handle the paperwork. You focus on your business.',
  },
  {
    number: '04',
    icon: 'ri-search-eye-line',
    title: 'Work With the Adjuster',
    desc: 'An adjuster assigned by the carrier will review the claim, inspect damages, and determine coverage based on your policy. This is a normal part of the process — be cooperative, provide documentation promptly, and contact us if anything seems off.',
    tip: 'We can advocate on your behalf if there are coverage disputes.',
  },
  {
    number: '05',
    icon: 'ri-checkbox-circle-line',
    title: 'Resolution & Payment',
    desc: 'Once the claim is approved, the carrier will issue payment or begin repairs based on your policy terms. Timelines vary by claim type and complexity — most straightforward claims resolve within 10–15 business days.',
    tip: 'Complex claims may take longer. We\'ll keep you updated throughout.',
  },
];

const whatToExpect = [
  {
    icon: 'ri-time-line',
    title: 'Typical Timeline',
    desc: 'Most straightforward claims resolve in 10–15 business days. Complex claims involving litigation or large losses may take longer.',
  },
  {
    icon: 'ri-user-line',
    title: 'Your Adjuster',
    desc: 'The adjuster works for the carrier — not you. That\'s why having an agent in your corner who understands your policy is critical.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Repair Contractors',
    desc: 'You can choose your own licensed contractor. We\'ll work directly with them and the carrier to coordinate repairs and payment.',
  },
  {
    icon: 'ri-bar-chart-line',
    title: 'Rate Impact',
    desc: 'Not all claims raise your rates. The impact depends on claim type and your history. Ask us before filing if you\'re unsure.',
  },
];

export default function HowToFileAClaimArticle() {
  useEffect(() => {
    document.title = 'How to File an Insurance Claim Step by Step | Summit Appalachia Insurance';

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) { metaDesc = document.createElement('meta'); metaDesc.setAttribute('name', 'description'); document.head.appendChild(metaDesc); }
    metaDesc.setAttribute('content', 'A simple walkthrough of the insurance claims process so you know exactly what to expect when you need Summit Appalachia Insurance most. 5 steps to filing a claim in KY, TN, VA.');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/resources/how-to-file-a-claim`);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      '@id': `${import.meta.env.VITE_SITE_URL}/resources/how-to-file-a-claim#howto`,
      url: `${import.meta.env.VITE_SITE_URL}/resources/how-to-file-a-claim`,
      name: 'How to File an Insurance Claim: Step by Step',
      description: 'A simple walkthrough of the insurance claims process so you know exactly what to expect when you need Summit Appalachia Insurance most.',
      step: [
        { '@type': 'HowToStep', position: 1, name: 'Report the Claim Immediately', text: 'Contact your insurance carrier or Summit Appalachia Insurance as soon as the incident happens.' },
        { '@type': 'HowToStep', position: 2, name: 'Gather Key Information', text: 'Collect date, time, description, photos, names of involved parties, and your policy number.' },
        { '@type': 'HowToStep', position: 3, name: 'Submit the Claim', text: 'We\'ll help file the claim with the carrier and make sure all details are accurate.' },
        { '@type': 'HowToStep', position: 4, name: 'Work With the Adjuster', text: 'An adjuster will review the claim, inspect damages, and determine coverage.' },
        { '@type': 'HowToStep', position: 5, name: 'Resolution & Payment', text: 'Once approved, the carrier will issue payment or begin repairs based on your policy.' },
      ],
      author: { '@type': 'Organization', name: 'Summit Appalachia Insurance' },
      publisher: {
        '@type': 'Organization',
        name: 'Summit Appalachia Insurance',
        url: import.meta.env.VITE_SITE_URL,
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-claim-howto';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.getElementById('schema-claim-howto')?.remove();
      const can = document.querySelector('link[rel="canonical"]');
      if (can) can.setAttribute('href', import.meta.env.VITE_SITE_URL ?? '/');
    };
  }, []);

  return (
    <div className="font-sans bg-[#000000]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[420px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/be4f167f59726ae89cae71bec05c9981.jpg"
            alt="How to File a Claim"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 pb-16 w-full">
          <p className="text-white/30 text-xs font-sans mb-3 uppercase tracking-widest">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors">Home</Link>
            {' '}/ <Link to="/resources" className="hover:text-[#D4AF37] transition-colors">Resources</Link>
            {' '}/ Guide
          </p>
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-sm">Guides</span>
            <span className="text-white/30 text-xs font-sans">4 min read · Oct 2025</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-white leading-tight">
            How to File a Claim:<br />
            <span className="text-[#D4AF37]">Step by Step</span>
          </h1>
          <p className="text-white/50 text-base font-sans mt-4 max-w-2xl leading-relaxed">
            A simple walkthrough so you know exactly what to expect when you need us most.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-14 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="border-l-2 border-[#D4AF37] pl-6 mb-12">
            <p className="text-white/70 text-base font-sans leading-relaxed">
              Filing a claim can feel overwhelming — especially when you're already dealing with an accident, loss, or damage. <strong className="text-white">The faster and more accurate your information, the smoother your claim process will be.</strong> This guide walks you through every step so there are no surprises.
            </p>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl text-white mb-2">The 5-Step Claims Process</h2>
          <p className="text-white/40 text-sm font-sans mb-10 leading-relaxed">
            Follow these steps in order. Each one builds on the last — skipping ahead or delaying any step can slow down your resolution.
          </p>
          <div className="space-y-5">
            {steps.map((step, i) => (
              <div key={i} className="bg-[#111111] border border-[#D4AF37]/15 rounded-md p-6 hover:border-[#D4AF37]/35 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                  <div className="flex-shrink-0 flex items-center gap-3 sm:flex-col sm:items-center sm:gap-1">
                    <span className="font-serif text-3xl font-bold text-[#D4AF37]/30 leading-none">{step.number}</span>
                    <div className="w-9 h-9 flex items-center justify-center text-[#D4AF37]">
                      <i className={`${step.icon} text-xl`} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-xl text-white mb-2">{step.title}</h3>
                    <p className="text-white/55 text-sm font-sans leading-relaxed mb-3">{step.desc}</p>
                    {step.checklist && (
                      <ul className="space-y-1.5 mb-3">
                        {step.checklist.map((item, j) => (
                          <li key={j} className="flex items-center gap-2 text-white/50 text-sm font-sans">
                            <div className="w-4 h-4 flex items-center justify-center text-[#D4AF37] flex-shrink-0">
                              <i className="ri-checkbox-circle-fill text-sm" />
                            </div>
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                    {step.tip && (
                      <div className="flex items-start gap-2 mt-3 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-sm px-4 py-2.5">
                        <div className="w-4 h-4 flex items-center justify-center text-[#D4AF37] flex-shrink-0 mt-0.5">
                          <i className="ri-lightbulb-line text-sm" />
                        </div>
                        <p className="text-[#D4AF37]/80 text-xs font-sans leading-relaxed">{step.tip}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 bg-[#000000]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl text-white mb-2">What to Expect</h2>
          <p className="text-white/40 text-sm font-sans mb-10 leading-relaxed">
            A few things most policyholders don't know until they're in the middle of a claim.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {whatToExpect.map((item, i) => (
              <div key={i} className="bg-[#111111] border border-[#D4AF37]/15 rounded-md p-6 hover:border-[#D4AF37]/35 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center text-[#D4AF37]">
                    <i className={`${item.icon} text-xl`} />
                  </div>
                  <h3 className="font-sans text-sm font-bold text-white">{item.title}</h3>
                </div>
                <p className="text-white/45 text-sm font-sans leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Tip */}
      <section className="py-14 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="bg-[#111111] border border-[#D4AF37]/30 rounded-md p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 flex items-center justify-center text-[#D4AF37]">
                <i className="ri-alert-line text-xl" />
              </div>
              <h3 className="font-sans text-sm font-bold text-white uppercase tracking-widest">Final Tip</h3>
            </div>
            <p className="text-white/60 text-base font-sans leading-relaxed">
              The faster and more accurate your information, the smoother your claim process will be. Don't guess — document everything, report immediately, and let us handle the carrier communication. That's what we're here for.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#000000] border-t border-[#D4AF37]/10">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Need to File a Claim Now?</h2>
          <p className="text-white/40 text-sm font-sans leading-relaxed mb-8 max-w-xl mx-auto">
            Summit Appalachia Insurance is here to guide you through every step. Contact us immediately and we'll make sure your claim is filed correctly and on time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/claims"
              className="px-8 py-3.5 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest rounded-md hover:bg-[#e8c84e] transition-colors whitespace-nowrap cursor-pointer"
            >
              Go to Claims Page
            </Link>
            <Link
              to="/resources"
              className="px-8 py-3.5 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-bold uppercase tracking-widest rounded-md hover:border-[#D4AF37] transition-colors whitespace-nowrap cursor-pointer"
            >
              Back to Resources
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
